<script>
// @ts-check

import { reformatDateString } from '@/helpers/dates'

export default {
  name: 'BusinessAccountOnboardingFormAdditional',
  props: {
    countryOptions: {
      /** @type {import('vue').PropType<Pick<CountryResource, 'code' | 'name'>[]>} */
      type: Array,
      required: true
    },
    company: {
      /** @type {import('vue').PropType<CompanyResource>} */
      type: Object,
      required: true
    },
    user: {
      /** @type {import('vue').PropType<UserResource & CompanyUserResource>} */
      type: Object,
      required: true
    },
    showEntrepreneurInformation: {
      type: Boolean
    }
  },
  emits: ['hideDialog'],
  computed: {
    companyFullAddress() {
      if (!this.company.address) {
        return '-'
      }
      return this.getFullAddress(this.company.address)
    },
    userFullAddress() {
      if (!this.user.address) {
        return '-'
      }
      return this.getFullAddress(this.user.address)
    },
    userFullName() {
      return `${this.user.firstName} ${this.user.lastName}`
    },
    companyNameAndID() {
      return `${this.company.name}, ${this.company.business_id}`
    },
    userBirthDate() {
      if (!this.user.date_of_birth) {
        return '-'
      }
      return reformatDateString(this.user.date_of_birth)
    }
  },
  methods: {
    /**
     * @param address {AddressResource}
     */
    getFullAddress(address) {
      const address1 = address.address
      const postOffice = address.post_office
      const zipCode = address.zip
      const country =
        this.countryOptions.find((item) => item.code === address.country)
          ?.name ?? '-'

      return `${address1}, ${zipCode}, ${postOffice}, ${country}`
    }
  }
}
</script>

<template>
  <div>
    <div
      class="mb-4 flex flex-col gap-y-4 pt-2 lg:flex-row lg:gap-x-8 lg:gap-y-0"
    >
      <div class="flex-1">
        <h3 class="heading-4 mb-2">
          {{
            $t('business_account.onboarding_form_additional.company_details')
          }}
        </h3>
        <p>
          <span class="mr-2 font-semibold">
            {{ $t('business_account.onboarding_form_additional.name') }}
          </span>
          <span>{{ companyNameAndID }}</span>
        </p>
        <p>
          <span class="mr-2 font-semibold">
            {{ $t('business_account.onboarding_form_additional.email') }}
          </span>
          <span>{{ company.email }}</span>
        </p>
        <p>
          <span class="mr-2 font-semibold">
            {{ $t('business_account.onboarding_form_additional.address') }}
          </span>
          <span>{{ companyFullAddress }}</span>
        </p>
      </div>
      <div v-if="showEntrepreneurInformation" class="flex-1">
        <h3 class="heading-4 mb-2">
          {{
            $t(
              'business_account.onboarding_form_additional.entrepreneur_details'
            )
          }}
        </h3>
        <p>
          <span class="mr-2 font-semibold">
            {{ $t('business_account.onboarding_form_additional.name') }}
          </span>
          <span>{{ userFullName }}</span>
        </p>
        <p>
          <span class="mr-2 font-semibold">
            {{ $t('business_account.onboarding_form_additional.birth_date') }}
          </span>
          <span>{{ userBirthDate }}</span>
        </p>
        <p>
          <span class="mr-2 font-semibold">
            {{ $t('business_account.onboarding_form_additional.address') }}
          </span>
          <span>{{ userFullAddress }}</span>
        </p>
      </div>
    </div>
    <p class="pb-2 text-xs">
      <span class="mr-2">
        {{ $t('business_account.onboarding_form_additional.footnote') }}
      </span>
      <RouterLink
        :to="{ name: 'settings' }"
        class="inline-block text-primary underline"
        @click="$emit('hideDialog')"
      >
        {{ $t('routes.settings') }}
      </RouterLink>
    </p>
  </div>
</template>
